import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Clock, Loader2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

import { submitParceiroToHubSpot } from "@/lib/hubspot";
import {
  trackCompleteRegistration,
  trackInitiateCheckout,
  trackLead,
  trackLeadDesqualificado,
  trackMQL,
} from "@/lib/meta-pixel";
import {
  CARTEIRA_OPTIONS,
  CERTIFICACAO_OPTIONS,
  parceirosFormSchema,
  type ParceirosFormData,
} from "./schema";
import { cn } from "@/lib/utils";
import { formatPhone } from "@/lib/phone";
import { buildWhatsappLink, WHATSAPP_PARCEIROS } from "@/lib/whatsapp";
import { WhatsappIcon } from "@/components/icons/WhatsappIcon";

// Score arbitrário do lead baseado no tamanho da carteira (BRL).
// Serve como sinal pro Meta otimizar a campanha por valor estimado.
const CARTEIRA_VALUE_MAP: Record<string, number> = {
  ate1milhao: 1,
  "1a5milhoes": 5,
  "5a20milhoes": 20,
  "20a100milhoes": 100,
  acima100milhoes: 500,
};

// Faixas que qualificam como MQL (carteira ≥ R$ 5M).
const MQL_CARTEIRAS = new Set([
  "5a20milhoes",
  "20a100milhoes",
  "acima100milhoes",
]);

type FormState = Partial<ParceirosFormData>;
type FieldErrors = Partial<Record<keyof ParceirosFormData, string>>;

const inputBase =
  "h-10 sm:h-12 bg-white border-transparent text-[#161617] placeholder:text-zinc-400 focus:border-[#B8934A] focus-visible:ring-[#B8934A] focus-visible:ring-offset-[#1F3D2E] rounded-xl text-base";

// Ordem de verificação pra saber qual campo focar/rolar primeiro quando o
// submit falha na validação (item 1: nunca bloquear via estilo, só via foco
// no primeiro campo com erro).
const FIELD_FOCUS_ORDER: (keyof ParceirosFormData)[] = [
  "fullname",
  "email",
  "phone",
  "ja_atua",
  "carteira_size",
  "lgpd_consent",
];

export const ParceirosV2HeroForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const trackedFirstFocus = useRef(false);

  const [data, setData] = useState<FormState>({});
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const set = <K extends keyof ParceirosFormData>(
    key: K,
    value: ParceirosFormData[K]
  ) => {
    setData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const onFirstFocus = () => {
    if (trackedFirstFocus.current) return;
    trackInitiateCheckout({
      content_name: "Programa de Parceiros Allu",
      content_category: "parceiros",
    });
    trackedFirstFocus.current = true;
  };

  // Pergunta qualificadora: "sim" libera o select de carteira (limpa pra
  // escolher); "nao" trava o select e marca a carteira como "nao_tem".
  const setJaAtua = (v: "sim" | "nao") => {
    set("ja_atua", v);
    set("carteira_size", v === "nao" ? "nao_tem" : "");
  };

  // Valida um campo no blur e mostra só o erro dele — evita o botão ficar
  // cinza "sem explicação" quando um campo está preenchido mas inválido
  // (ex.: e-mail malformado).
  const validateField = (key: keyof ParceirosFormData) => {
    const parsed = parceirosFormSchema.safeParse(data);
    const issue = parsed.success
      ? undefined
      : parsed.error.issues.find((i) => i.path[0] === key);
    setErrors((prev) => {
      const next = { ...prev };
      if (issue) next[key] = issue.message;
      else delete next[key];
      return next;
    });
  };

  // Estado reativo que dirige o botão.
  const isValid = parceirosFormSchema.safeParse(data).success;
  const isMQL = data.carteira_size
    ? MQL_CARTEIRAS.has(data.carteira_size)
    : false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parceirosFormSchema.safeParse(data);

    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      parsed.error.issues.forEach((issue) => {
        const key = issue.path[0] as keyof ParceirosFormData;
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      });
      setErrors(fieldErrors);
      toast({
        title: "Confira os dados",
        description:
          parsed.error.issues[0]?.message ?? "Preencha todos os campos.",
        variant: "destructive",
      });

      // Não bloqueia visualmente o botão — em vez disso, rola e foca no
      // primeiro campo com erro, na ordem em que aparecem no formulário.
      const firstErrorKey = FIELD_FOCUS_ORDER.find((k) => fieldErrors[k]);
      const targetId =
        firstErrorKey === "ja_atua" ? "ja_atua_group" : firstErrorKey;
      const target = targetId ? document.getElementById(targetId) : null;
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => target?.focus({ preventScroll: true }), 300);

      return;
    }

    setIsSubmitting(true);

    // Dispara a conversão no Pixel ANTES do envio pro HubSpot.
    // Motivo: o submit passa por uma Edge Function (browser → /api/parceiros-lead →
    // HubSpot CRM), que pode levar 1-2s. Se o trackLead viesse depois desse await e
    // logo antes do navigate, o beacon do fbq podia não sair a tempo (race com a
    // navegação SPA) — resultado: lead no HubSpot, mas 0 resultado no Meta.
    // Disparando aqui, o evento sai no clique, com folga, independente do backend.
    const value = CARTEIRA_VALUE_MAP[parsed.data.carteira_size] ?? 1;

    // event_id compartilhado: o mesmo Lead sai do Pixel (browser) e do CAPI
    // (servidor, em /api/parceiros-lead). Meta deduplica pelo event_id, então
    // a cobertura sobe sem contar lead duas vezes. Pro MQL, sufixo "-mql" pra
    // distinguir (event_id é único POR nome de evento).
    const eventID =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Advanced Matching — fbq normaliza/hasheia internamente; passar valor cru.
    const [firstName, ...lastParts] = parsed.data.fullname.trim().split(/\s+/);
    const userData = {
      em: parsed.data.email,
      ph: parsed.data.phone.replace(/\D/g, ""),
      fn: firstName,
      ln: lastParts.join(" ") || undefined,
      country: "br",
    };

    // "Não atuo no mercado" (carteira nao_tem) = lead ruim pro Meta. NÃO dispara
    // o "Lead" padrão (senão o algoritmo otimiza buscando mais gente assim) —
    // dispara LeadDesqualificado só pra registro/exclusão de público.
    const isDesqualificado = parsed.data.carteira_size === "nao_tem";

    if (isDesqualificado) {
      trackLeadDesqualificado(
        {
          content_name: "Programa de Parceiros Allu",
          content_category: "parceiros",
          currency: "BRL",
          value: 0,
        },
        { eventID, userData }
      );
    } else {
      trackLead(
        {
          content_name: "Programa de Parceiros Allu",
          content_category: "parceiros",
          currency: "BRL",
          value,
          carteira_size: parsed.data.carteira_size,
          lead_quality: isMQL ? "mql" : "regular",
        },
        { eventID, userData }
      );
    }

    if (isMQL) {
      trackMQL(
        {
          content_name: "Programa de Parceiros Allu",
          carteira_size: parsed.data.carteira_size,
          currency: "BRL",
          value,
        },
        { eventID: `${eventID}-mql`, userData }
      );
    }

    try {
      await submitParceiroToHubSpot(parsed.data, eventID);

      // Lead de carteira qualificada (MQL) → direto pro WhatsApp da equipe.
      // O HubSpot já capturou acima, então mesmo que a pessoa não mande a
      // mensagem, o lead está no CRM e a equipe liga.
      if (isMQL) {
        const carteiraLabel =
          CARTEIRA_OPTIONS.find((o) => o.value === parsed.data.carteira_size)
            ?.label ?? "";
        const msg = `Olá! Meu nome é ${parsed.data.fullname}, tenho uma carteira de ${carteiraLabel} e gostaria de conhecer o programa de parceiros do Allu Invest.`;
        trackCompleteRegistration({
          content_name: "Programa de Parceiros Allu",
          content_category: "parceiros",
        });
        window.location.href = buildWhatsappLink(msg, WHATSAPP_PARCEIROS);
        return;
      }

      // O flag avisa a página de obrigado pra NÃO disparar CompleteRegistration
      // — desqualificado não pode contar como conversão em nenhum evento padrão.
      navigate("/parceiros/obrigado", {
        state: isDesqualificado ? { desqualificado: true } : undefined,
      });
    } catch (err) {
      console.error("[ParceirosV2] HubSpot submit error:", err);
      toast({
        title: "Erro ao enviar",
        description:
          "Tente novamente em instantes. Se persistir, fale com a gente pelo e-mail.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      id="form"
      onSubmit={handleSubmit}
      noValidate
      className="bg-[#1F3D2E] rounded-3xl p-4 sm:p-6 w-full shadow-2xl shadow-[#1F3D2E]/25 ring-1 ring-[#163024]/50"
    >
      <div className="mb-3">
        <h2
          className="text-base sm:text-xl font-semibold text-white tracking-tight"
          style={{
            fontFamily:
              '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
            letterSpacing: "-0.02em",
          }}
        >
          Aplicação para parceria
        </h2>
        <p className="text-xs sm:text-sm text-[#F4EEE4]/80 mt-1 leading-relaxed">
          <Clock className="inline w-3.5 h-3.5 -mt-0.5 mr-1 text-[#B8934A]" />
          Retornamos em até{" "}
          <span className="font-bold text-[#B8934A]">1 dia útil</span> para
          uma conversa 1:1.
        </p>
      </div>

      <div className="space-y-2 sm:space-y-3">
        <div className="space-y-1">
          <Label
            htmlFor="fullname"
            className="text-white text-sm font-medium"
          >
            Nome completo <span className="text-[#B8934A]">*</span>
          </Label>
          <Input
            id="fullname"
            value={data.fullname ?? ""}
            onChange={(e) => set("fullname", e.target.value)}
            onFocus={onFirstFocus}
            onBlur={() => validateField("fullname")}
            placeholder="Seu nome e sobrenome"
            autoComplete="name"
            className={inputBase}
            aria-invalid={!!errors.fullname}
          />
          {errors.fullname && (
            <p className="text-xs text-[#fca5a5] mt-1">{errors.fullname}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="email" className="text-white text-sm font-medium">
              E-mail profissional <span className="text-[#B8934A]">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={data.email ?? ""}
              onChange={(e) => set("email", e.target.value)}
              onFocus={onFirstFocus}
              onBlur={() => validateField("email")}
              placeholder="voce@empresa.com.br"
              autoComplete="email"
              className={inputBase}
              aria-invalid={!!errors.email}
            />
            {errors.email && (
              <p className="text-xs text-[#fca5a5] mt-1">{errors.email}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="phone" className="text-white text-sm font-medium">
              Telefone (com DDD) <span className="text-[#B8934A]">*</span>
            </Label>
            <Input
              id="phone"
              type="tel"
              inputMode="tel"
              value={data.phone ?? ""}
              onChange={(e) => set("phone", formatPhone(e.target.value))}
              onFocus={onFirstFocus}
              onBlur={() => validateField("phone")}
              placeholder="(11) 99999-9999"
              autoComplete="tel"
              className={inputBase}
              aria-invalid={!!errors.phone}
            />
            {errors.phone && (
              <p className="text-xs text-[#fca5a5] mt-1">{errors.phone}</p>
            )}
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-white text-xs sm:text-sm font-medium">
            Você já trabalha no mercado de investimentos?{" "}
            <span className="text-[#B8934A]">*</span>
          </Label>
          <div
            id="ja_atua_group"
            tabIndex={-1}
            className="grid grid-cols-2 gap-3 outline-none"
          >
            {(["sim", "nao"] as const).map((opt) => {
              const active = data.ja_atua === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    onFirstFocus();
                    setJaAtua(opt);
                  }}
                  aria-pressed={active}
                  className={cn(
                    "h-10 sm:h-12 rounded-xl text-base font-medium border transition-colors duration-200 inline-flex items-center justify-center gap-1.5",
                    active
                      ? "bg-[#B8934A] text-[#1F3D2E] border-[#B8934A]"
                      : "bg-transparent text-white border-white/30 hover:bg-white/10"
                  )}
                >
                  {active && <Check className="w-4 h-4" strokeWidth={3} />}
                  {opt === "sim" ? "Sim" : "Não"}
                </button>
              );
            })}
          </div>
          {errors.ja_atua && (
            <p className="text-xs text-[#fca5a5] mt-1">{errors.ja_atua}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label
            htmlFor="certificacao"
            className="text-white text-xs sm:text-sm font-medium"
          >
            Qual certificação do mercado financeiro você possui?{" "}
            <span className="text-[#F4EEE4]/50 font-normal">(opcional)</span>
          </Label>
          <Select
            value={data.certificacao ?? ""}
            onValueChange={(v) => set("certificacao", v)}
          >
            <SelectTrigger
              id="certificacao"
              className={inputBase}
              onFocus={onFirstFocus}
            >
              <SelectValue placeholder="Selecione uma opção" />
            </SelectTrigger>
            <SelectContent>
              {CERTIFICACAO_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label
            htmlFor="carteira_size"
            className="text-white text-xs sm:text-sm font-medium"
          >
            Tamanho da carteira sob custódia atualmente{" "}
            <span className="text-[#B8934A]">*</span>
          </Label>
          {/* Só monta o Select quando a carteira é de fato escolhível (mesmo
              arranjo da /parceiros). Um Select desabilitado com value
              "nao_tem" quebrava de duas formas: o <select> oculto do Radix
              (BubbleSelect) emitia um change com "" — apagando a carteira e
              travando o submit de quem responde "Não" — e o trigger ficava em
              branco, porque "nao_tem" não tem SelectItem correspondente. */}
          {data.ja_atua === "sim" ? (
            <Select
              value={data.carteira_size ?? ""}
              onValueChange={(v) => set("carteira_size", v)}
            >
              <SelectTrigger
                id="carteira_size"
                className={inputBase}
                aria-invalid={!!errors.carteira_size}
                onFocus={onFirstFocus}
              >
                <SelectValue placeholder="Selecione uma faixa" />
              </SelectTrigger>
              <SelectContent>
                {CARTEIRA_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div
              aria-disabled="true"
              className={cn(
                inputBase,
                // Branco sólido como os demais inputs — o bg-white/50 da v1
                // some sobre o verde escuro deste form.
                "flex items-center px-4 text-zinc-400 cursor-not-allowed select-none"
              )}
            >
              {data.ja_atua === "nao"
                ? "Não se aplica"
                : "Responda a pergunta acima"}
            </div>
          )}
          {errors.carteira_size && (
            <p className="text-xs text-[#fca5a5] mt-1">{errors.carteira_size}</p>
          )}
        </div>

        <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
          <Checkbox
            id="lgpd_consent"
            checked={!!data.lgpd_consent}
            onCheckedChange={(v) => set("lgpd_consent", v === true)}
            className="mt-0.5 h-[18px] w-[18px] rounded-[4px] border-2 border-[#F4EEE4]/60 bg-white/5 data-[state=checked]:bg-[#B8934A] data-[state=checked]:border-[#B8934A] data-[state=checked]:text-[#1F3D2E]"
            aria-invalid={!!errors.lgpd_consent}
          />
          <span className="text-xs text-[#F4EEE4]/80 leading-relaxed">
            Li e autorizo o tratamento dos meus dados conforme a{" "}
            <a
              href="/privacidade"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white underline underline-offset-2 hover:text-[#B8934A]"
            >
              Política de Privacidade
            </a>
            .
          </span>
        </label>
        {errors.lgpd_consent && (
          <p className="text-xs text-[#fca5a5]">{errors.lgpd_consent}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full mt-1 inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm h-10 sm:h-12 shadow-md transition-colors bg-[#B8934A] hover:bg-[#a37f3f] text-[#1F3D2E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B8934A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1F3D2E] disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Enviando…
            </>
          ) : isMQL ? (
            <>
              <WhatsappIcon className="w-4 h-4" />
              Falar no WhatsApp agora
            </>
          ) : (
            <>
              Quero receber contato
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>

        {!isValid ? (
          <p className="text-[11px] text-[#F4EEE4]/70 text-center leading-snug">
            Preencha todos os campos para continuar.
          </p>
        ) : isMQL ? (
          <p className="text-[11px] text-[#F4EEE4]/70 text-center leading-snug">
            Você será direcionado ao WhatsApp da nossa equipe.
          </p>
        ) : null}

        <p className="text-[11px] text-[#F4EEE4]/60 text-center leading-snug">
          Conteúdo institucional B2B. Não destinado ao investidor final.
        </p>
      </div>
    </form>
  );
};
